/**
 * Scan tab — camera capture + LOCAL Tesseract OCR juror-questionnaire flow.
 *
 * PRIVACY / SECURITY CRITICAL. Faithful port of the monolith Scan tab, with two
 * deliberate changes:
 *   1. OCR is LOCAL-ONLY. The legacy Claude-proxy (`runClaudeVisionOCR`, a
 *      localhost:8001 fetch) and Google Cloud Vision (`runLocalImageOCR`) paths
 *      are REMOVED, along with the `CLAUDE_API_KEY` prompt/localStorage. All
 *      recognition now runs on-device via `recognizeText` (Tesseract.js).
 *   2. The IndexedDB OCR cache (hashImage/ocrCacheGet/ocrCachePut) is retained.
 *
 * The privacy pipeline is preserved verbatim: OCR text -> redactRawText ->
 * extractPermittedFields(text, panelNumber). The canvas holding the photo is
 * wiped (filled black, cleared, sized to 0) immediately after recognition so the
 * captured image never lingers in memory.
 */

import type { Juror } from '@/types';
import type { ScrubbedForm, FieldResult } from '@/types/form';
import { store, panelContext } from '@/app/store';
import { scoreJuror, getScoreLabel, getFlags } from '@/core/scoring';
import { redactRawText, extractPermittedFields } from '@/core/privacy';
import { recognizeText } from '@/ocr/ocr';
import { hashImage, ocrCacheGet, ocrCachePut } from '@/ocr/ocr-cache';
import { loadJurorArray, updatePanelBadge, renderPanel } from '@/features/panel';
import { byId } from '@/ui/dom';
import { showToast, confirmApp } from '@/ui/overlays';
import { showPage } from '@/ui/router';
import { escapeHTML } from '@/utils/formatters';

// ── Module-scoped state (was monolith globals near line 3274) ────────────────
// The juror currently awaiting confirmation in the review card.
let pendingOCRJuror: Partial<Juror> | null = null;
// Template-path only: the raw extracted field map + the fields that need human
// review (each carrying a TRANSIENT crop image). Cleared on save/discard; the
// crop images are shown in the review card and NEVER persisted to storage.
let pendingScrubbed: ScrubbedForm | null = null;
let pendingReview: FieldResult[] = [];

/** Drop the pending review state, releasing the transient crop data URLs. */
function clearPending(): void {
  pendingOCRJuror = null;
  pendingScrubbed = null;
  pendingReview = [];
}
// Auto-incrementing panel counter used for the "Juror N" labels and panel IDs.
let scanJurorCounter = 1;
// Live getUserMedia stream for the Facts (indictment / police report) camera.
let factsCamStream: MediaStream | null = null;
// NOTE: the monolith also declared `camStream`, `CLAUDE_API_KEY` and `ocrDb`.
// `camStream` was never used by the scan flow (openScanCamera uses a native file
// input, stopCamera is a no-op); the API-key/db globals belonged to the removed
// cloud-OCR paths and the ocr-cache module. All three are intentionally dropped.

// Load an image File into an <img>, resolving once decoded. Revokes the object
// URL in both success and failure paths.
function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>(function (resolve, reject) {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

// Fill-black, clear and zero a canvas so no captured pixels remain.
function wipeCanvas(c: HTMLCanvasElement, ctx: CanvasRenderingContext2D | null): void {
  if (ctx) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.clearRect(0, 0, c.width, c.height);
  }
  c.width = 0;
  c.height = 0;
}

// Live getUserMedia stream for the desktop webcam scan flow (see startScanWebcam).
let scanCamStream: MediaStream | null = null;
// Guards one-time registration of the background/hide privacy listeners.
let scanPrivacyHooked = false;

// prefersNativeCapture — decide between the native camera picker (phones, the
// Capacitor native app) and a live desktop webcam. On touch/native the file
// input with capture="environment" opens the OS camera, which is the best UX;
// on a desktop it would only open a file picker, so there we use getUserMedia.
function prefersNativeCapture(): boolean {
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  const isNative = Boolean(w.Capacitor?.isNativePlatform?.());
  const coarsePointer = typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
  const noWebcamApi = !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia;
  return isNative || coarsePointer || noWebcamApi;
}

// openScanCamera — entry point for the "Juror N" button. Phones/native open the
// OS camera via the file input; desktop opens a live webcam preview.
export function openScanCamera(): void {
  const progress = byId('ocr-progress');
  if (prefersNativeCapture()) {
    if (progress) progress.textContent = 'Opening camera...';
    const inp = byId<HTMLInputElement>('cam-file-input');
    if (inp) { inp.value = ''; inp.click(); } // reset so same file can re-trigger
    return;
  }
  void startScanWebcam();
}

// startScanWebcam — desktop live capture. Shows the preview and enables Capture.
// Falls back to the file picker if the webcam is unavailable or denied.
async function startScanWebcam(): Promise<void> {
  ensureScanPrivacyHooks();
  const progress = byId('ocr-progress');
  const wrap = byId('scan-cam-wrap');
  const toggle = byId('btn-cam-toggle');
  try {
    if (progress) progress.textContent = 'Starting camera…';
    if (wrap) wrap.style.display = 'block';
    scanCamStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1440 } }
    });
    const v = byId<HTMLVideoElement>('scan-cam-video');
    if (v) { v.srcObject = scanCamStream; await v.play(); }
    if (toggle) toggle.style.display = 'none';
    const cap = byId<HTMLButtonElement>('btn-scan-capture');
    if (cap) cap.disabled = false;
    if (progress) progress.textContent = 'Camera ready — align the questionnaire and click Capture';
  } catch {
    // No webcam / permission denied → fall back to choosing an image file.
    stopScanCamera();
    if (progress) progress.textContent = 'Camera unavailable — choose an image instead';
    const inp = byId<HTMLInputElement>('cam-file-input');
    if (inp) { inp.value = ''; inp.click(); }
  }
}

// stopScanCamera — release the webcam and restore the button. Safe to call any
// time (idempotent). Also the router page-leave / background privacy handler.
export function stopScanCamera(): void {
  if (scanCamStream) { scanCamStream.getTracks().forEach((t) => t.stop()); scanCamStream = null; }
  const v = byId<HTMLVideoElement>('scan-cam-video'); if (v) v.srcObject = null;
  const wrap = byId('scan-cam-wrap'); if (wrap) wrap.style.display = 'none';
  const toggle = byId('btn-cam-toggle'); if (toggle) toggle.style.display = '';
  const cap = byId<HTMLButtonElement>('btn-scan-capture'); if (cap) cap.disabled = true;
}

// captureScanWebcam — grab the current frame, immediately stop the live camera
// and wipe the canvas, then run the SAME on-device OCR pipeline as a photo.
export async function captureScanWebcam(): Promise<void> {
  const v = byId<HTMLVideoElement>('scan-cam-video');
  const c = byId<HTMLCanvasElement>('cam-canvas');
  if (!scanCamStream || !v || !v.videoWidth || !c) { showToast('Camera not ready'); return; }
  const progress = byId('ocr-progress');
  c.width = v.videoWidth; c.height = v.videoHeight;
  const ctx = c.getContext('2d');
  if (ctx) ctx.drawImage(v, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => c.toBlob(resolve, 'image/jpeg', 0.95));
  wipeCanvas(c, ctx);   // captured pixels never linger in the canvas
  stopScanCamera();     // turn the live camera off the moment we have the frame
  if (!blob) { if (progress) progress.textContent = 'Capture failed — click Juror to retry'; return; }
  await processScannedImage(blob);
}

// ensureScanPrivacyHooks — stop the webcam when the app is hidden/backgrounded,
// mirroring the app's "wipe on exit" privacy posture. Registered once, lazily.
function ensureScanPrivacyHooks(): void {
  if (scanPrivacyHooked) return;
  scanPrivacyHooked = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') stopScanCamera();
  });
  document.addEventListener('pagehide', () => stopScanCamera());
}

// stopCamera — router page-leave hook for the scan tab: release any live camera.
export function stopCamera(): void {
  stopScanCamera();
  stopFactsCamera();
}

// handleCameraCapture — fires when the user takes/selects a photo.
//
// PRIMARY path: template-driven, on-device extraction (form-parser.ts) — reads
// ONLY the curated JURY_FORM_TEMPLATE boxes (checkbox density + TrOCR for the
// handwritten fields), so full-page text (name/address/employer) is never even
// OCR'd. Marked checkboxes are detected directly, so a filled form maps to the
// juror's ACTUAL answers rather than merely the printed option labels.
//
// FALLBACK path: if the template pipeline can't run (e.g. the handwriting model
// fails to load, or the image can't be decoded by RawImage), fall back to the
// legacy full-page Tesseract OCR + privacy redaction/extraction. That path is
// cached in IndexedDB; the template path is not (parseForm is idempotent and a
// re-scan of the identical image is rare).
export async function handleCameraCapture(evt: Event): Promise<void> {
  const input = evt.target as HTMLInputElement | null;
  const file = input && input.files && input.files[0];
  if (!file) {
    const progress = byId('ocr-progress');
    if (progress) progress.textContent = 'No image selected';
    return;
  }
  await processScannedImage(file);
}

// processScannedImage — shared OCR pipeline for BOTH capture paths (native photo
// / file pick via handleCameraCapture, and desktop webcam via captureScanWebcam).
// Primary: template-driven on-device extraction; fallback: full-page Tesseract.
async function processScannedImage(blob: Blob): Promise<void> {
  const progress = byId('ocr-progress');
  const btn = byId<HTMLButtonElement>('btn-cam-toggle');
  if (btn) btn.disabled = true;
  const reviewCard = byId('ocr-review-card');
  if (reviewCard) reviewCard.style.display = 'none';

  try {
    if (progress) progress.textContent = 'Reading Juror ' + scanJurorCounter + ' on-device (handwriting)…';
    const parsed = await parseQuestionnaire(blob, scanJurorCounter);
    pendingOCRJuror = parsed.juror;
    pendingScrubbed = parsed.scrubbed;
    pendingReview = parsed.review;
  } catch (tplErr) {
    if (progress) progress.textContent = 'Falling back to text OCR for Juror ' + scanJurorCounter + '…';
    try {
      pendingOCRJuror = await tesseractExtract(blob);
      pendingScrubbed = null;
      pendingReview = [];
    } catch (e) {
      if (progress) progress.textContent = 'Could not read image — tap to retry';
      if (btn) btn.disabled = false;
      return;
    }
  }

  showOCRReview(pendingOCRJuror);
  if (progress) progress.textContent = 'Review extracted info below';
  if (btn) btn.disabled = false;
}

// parseQuestionnaire — PRIMARY OCR path. Dynamically imports the heavy TrOCR /
// transformers pipeline so it only loads the first time the user scans a form
// (keeps app startup light). Returns a Partial<Juror> ready for the review card.
interface ParsedQuestionnaire {
  juror: Partial<Juror>;
  scrubbed: ScrubbedForm;
  /** Needs-review fields carrying a transient crop image for manual entry. */
  review: FieldResult[];
}

async function parseQuestionnaire(file: Blob, panelNumber: number): Promise<ParsedQuestionnaire> {
  const [{ parseForm }, { JURY_FORM_TEMPLATE }, { scrubbedToJuror }] = await Promise.all([
    import('@/ocr/form-parser'),
    import('@/ocr/jury-form-template'),
    import('@/ocr/questionnaire-map')
  ]);
  const { scrubbed, results } = await parseForm(file, JURY_FORM_TEMPLATE);
  const review = results.filter((r) => r.needsReview && r.image);
  return { juror: scrubbedToJuror(scrubbed, panelNumber), scrubbed, review };
}

// tesseractExtract — FALLBACK OCR path. Legacy full-page Tesseract + the verbatim
// two-step privacy filter (redactRawText → extractPermittedFields), with the
// IndexedDB cache and immediate canvas wipe preserved from the original flow.
async function tesseractExtract(file: Blob): Promise<Partial<Juror>> {
  const imageHash = await hashImage(file);
  // The cache stores REDACTED text only — raw OCR (which may contain a name or
  // address printed on the form) is never persisted. Tagging the juror with
  // imageHash lets per-juror deletion purge this one entry later.
  const cachedRedacted = await ocrCacheGet(imageHash);
  if (cachedRedacted) {
    const juror = extractPermittedFields(cachedRedacted, scanJurorCounter);
    juror.ocrHash = imageHash;
    return juror;
  }
  const c = byId<HTMLCanvasElement>('cam-canvas');
  if (!c) throw new Error('scan canvas missing');
  const ctx = c.getContext('2d');
  try {
    const img = await loadImage(file);
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    if (ctx) ctx.drawImage(img, 0, 0);
    img.src = ''; // dereference source immediately

    let rawText = '';
    let recognized = false;
    // LOCAL Tesseract OCR only — no cloud/network calls.
    try {
      rawText = await recognizeText(c);
      recognized = true;
    } catch (e) {
      rawText = '[OCR unavailable — please enter info manually]';
    }
    // Wipe canvas immediately after OCR — photo is gone
    wipeCanvas(c, ctx);
    // Redact PII first, THEN cache/extract — unredacted text never persists.
    const redacted = redactRawText(rawText);
    if (recognized) await ocrCachePut(imageHash, redacted);
    const juror = extractPermittedFields(redacted, scanJurorCounter);
    if (recognized) juror.ocrHash = imageHash; // no cache entry ⇒ no tag
    return juror;
  } catch (e) {
    wipeCanvas(c, ctx);
    throw e;
  }
}

// showOCRReview — renders extracted fields + a scoring preview in the review card.
function showOCRReview(j: Partial<Juror>): void {
  const titleEl = byId('ocr-review-title');
  if (titleEl) titleEl.textContent = 'Juror ' + scanJurorCounter + ' — Review';
  const state = store.getState();
  const ctx = panelContext();
  // ── Run analysis on pending juror so user sees score before saving ──
  const sc = scoreJuror(j as Juror, ctx);
  const sl = getScoreLabel(sc, state.panelLens);
  const flags = getFlags(j as Juror, ctx);
  const sideLabel = state.panelLens === 'defense' ? 'Defense' : 'Prosecution';
  // Score banner
  const scoreColor = sl.cls.indexOf('strong-def') !== -1 ? '#1e40af' : sl.cls.indexOf('def') !== -1 ? '#3b82f6' : sl.cls.indexOf('strong-pros') !== -1 ? '#991b1b' : sl.cls.indexOf('pros') !== -1 ? '#ef4444' : '#6b7280';
  let html = '<div style="display:flex;align-items:center;gap:10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;margin-bottom:10px;">';
  html += '<div style="font-size:24px;font-weight:800;color:' + scoreColor + ';min-width:36px;text-align:center;">' + sl.label + '</div>';
  html += '<div><div style="font-size:10px;font-weight:700;color:var(--text-muted);text-transform:uppercase;">' + sideLabel + ' Score</div>';
  if (flags.length) {
    html += '<div style="font-size:11px;color:var(--text-secondary);margin-top:2px;">' + flags.map(function (f) {
      const cc = f.side === 'def' ? '#3b82f6' : f.side === 'pros' ? '#ef4444' : '#6b7280';
      return '<span style="color:' + cc + ';font-weight:600;">' + escapeHTML(f.label) + '</span>';
    }).join(' · ') + '</div>';
  } else {
    html += '<div style="font-size:11px;color:var(--text-muted);margin-top:2px;">No flags</div>';
  }
  html += '</div></div>';
  // Extracted fields grid
  const allRows: [string, string | undefined][] = [
    ['Panel ID', j.panelId], ['Gender', j.gender], ['Age', j.age],
    ['Education', j.edu], ['Employment', j.empStatus],
    ['Business Type', j.bizType], ['Occupation', j.occ],
    ['Household Size', j.hhSize], ['Parent/Guardian', j.parent],
    ['Spouse Age', j.spouseAge], ['Spouse Education', j.spouseEdu],
    ['Prior Jury', j.jury], ['LE Connection', j.le],
    ['Law Employment', j.lawEmp], ['Crime Victim', j.victim],
    ['Accused', j.accused], ['Fair/Impartial', j.fair],
    ['Case Knowledge', j.caseKnow]
  ];
  const rows = allRows.filter(function (r) { return r[1] && r[1] !== 'None' && r[1] !== 'No' && r[1] !== 'None stated' && r[1] !== 'Not found'; });
  html += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:5px 10px;">';
  rows.forEach(function (r) {
    html += '<div style="padding:3px 0;">'
      + '<div style="font-size:9px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;">' + r[0] + '</div>'
      + '<div style="font-size:12px;color:var(--text-primary);font-weight:500;">' + escapeHTML(r[1] as string) + '</div>'
      + '</div>';
  });
  if (!rows.length) html += '<div style="font-size:12px;color:var(--text-muted);grid-column:1/-1;padding:6px 0;">No fields extracted — try retaking the photo with better lighting.</div>';
  html += '</div>';
  // ── Needs-review: fields the OCR could not resolve. No typing here — this
  //    screen is used live in court (tablet, tap-only); an unresolved field is
  //    just queued for the background re-scan (below) and reviewed later,
  //    off the courtroom flow. The crop image is shown for context only.
  if (pendingReview.length) {
    html += '<div style="margin-top:12px;padding-top:10px;border-top:1px solid #e2e8f0;">';
    html += '<div style="font-size:10px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:.4px;margin-bottom:8px;">⚠ Needs review — ' + pendingReview.length + ' field' + (pendingReview.length === 1 ? '' : 's') + ' the scanner could not read (queued for background re-scan)</div>';
    pendingReview.forEach(function (r) {
      html += '<div style="margin-bottom:10px;">'
        + '<div style="font-size:9px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:3px;">' + escapeHTML(r.label) + '</div>'
        + (r.image ? '<img src="' + r.image + '" alt="scanned field" style="display:block;max-width:100%;height:auto;border:1px solid #e2e8f0;border-radius:4px;background:#fff;" />' : '')
        + '</div>';
    });
    html += '</div>';
  }
  const fieldsEl = byId('ocr-review-fields');
  if (fieldsEl) fieldsEl.innerHTML = html;
  const card = byId('ocr-review-card');
  if (card) {
    card.style.display = '';
    // Scroll the review card into view
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

// scanConfirmSave — user taps "Save Juror". Saves the juror as extracted; any
// field the scanner couldn't read is queued for background re-OCR (no typed
// correction happens here — this screen is used live in court, tap-only).
export async function scanConfirmSave(): Promise<void> {
  if (!pendingOCRJuror) { showToast('Nothing to save'); return; }
  const juror = pendingOCRJuror;
  const scrubbed = pendingScrubbed; // capture before clearPending
  const review = pendingReview;
  const savedPanelId = juror.panelId;
  ocrToPanel(juror);

  // Queue every unresolved field crop for background re-OCR, keyed to the
  // saved juror.
  if (scrubbed && review.length) {
    const saved = store.getState().jurors.find((j) => j.panelId === savedPanelId);
    const unresolved = review.filter((r) => r.image && !scrubbed[r.id]);
    if (saved && unresolved.length) {
      const [fr, engine] = await Promise.all([
        import('@/ocr/field-review'),
        import('@/ocr/field-review-engine')
      ]);
      const queuedAt = new Date().toISOString();
      await Promise.all(
        unresolved.map((r) =>
          fr.queueField({ jurorId: saved.id, fieldId: r.id, label: r.label, image: r.image as string, queuedAt })
        )
      );
      void engine.startFieldReview();
    }
  }

  clearPending();
  const card = byId('ocr-review-card');
  if (card) card.style.display = 'none';
}

// scanRetake — user taps "Retake Photo".
export function scanRetake(): void {
  clearPending();
  const card = byId('ocr-review-card');
  if (card) card.style.display = 'none';
  const progress = byId('ocr-progress');
  if (progress) progress.textContent = 'Tap to photograph the questionnaire';
  openScanCamera();
}

// ============================================================
//  FACTS FROM INDICTMENT / POLICE REPORT -- OCR CAMERA (full-text, no field filter)
// ============================================================
export async function toggleFactsCamera(): Promise<void> {
  const btn = byId('btn-facts-cam-toggle');
  const wrap = byId('facts-cam-wrap');
  if (factsCamStream) { stopFactsCamera(); return; }
  const progress = byId('facts-ocr-progress');
  try {
    if (progress) progress.textContent = 'Starting camera...';
    if (wrap) wrap.style.display = 'block';
    factsCamStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1440 } } });
    const v = byId<HTMLVideoElement>('facts-cam-video');
    if (v) { v.srcObject = factsCamStream; await v.play(); }
    if (btn) btn.textContent = '✖ Stop Camera';
    const cap = byId<HTMLButtonElement>('btn-facts-capture');
    if (cap) cap.disabled = false;
    if (progress) progress.textContent = 'Camera ready -- align document and tap Capture';
  } catch (e) {
    if (progress) progress.textContent = 'Camera error: ' + (e instanceof Error ? e.message : String(e));
  }
}

function stopFactsCamera(): void {
  if (factsCamStream) { factsCamStream.getTracks().forEach(function (t) { t.stop(); }); factsCamStream = null; }
  const v = byId<HTMLVideoElement>('facts-cam-video'); if (v) v.srcObject = null;
  const btn = byId('btn-facts-cam-toggle'); if (btn) btn.textContent = '📷 Import from Photo (OCR)';
  const capBtn = byId<HTMLButtonElement>('btn-facts-capture'); if (capBtn) capBtn.disabled = true;
  const wrap = byId('facts-cam-wrap'); if (wrap) wrap.style.display = 'none';
  const prog = byId('facts-ocr-progress'); if (prog) prog.textContent = 'Camera off';
}

export async function captureFactsOCR(): Promise<void> {
  const v = byId<HTMLVideoElement>('facts-cam-video');
  const c = byId<HTMLCanvasElement>('facts-canvas');
  if (!factsCamStream || !v || !v.videoWidth || !c) { showToast('Camera not ready'); return; }
  c.width = v.videoWidth; c.height = v.videoHeight;
  const ctx = c.getContext('2d');
  if (ctx) ctx.drawImage(v, 0, 0);
  const progress = byId('facts-ocr-progress');
  if (progress) progress.textContent = 'Running OCR on-device...';
  const btn = byId<HTMLButtonElement>('btn-facts-capture');
  const origLabel = btn ? (btn.textContent || '') : '';
  if (btn) { btn.textContent = 'Processing...'; btn.disabled = true; }
  try {
    // LOCAL Tesseract OCR only.
    const rawText = await recognizeText(c);
    // Wipe canvas immediately after recognition
    wipeCanvas(c, ctx);
    // NOTE: This field extracts ALL text with no PII field filtering -- this is
    // typed legal document text (indictment / police report), not a juror
    // questionnaire, so the field-level privacy filter does not apply here.
    const ta = byId<HTMLTextAreaElement>('inp-indictment-facts');
    if (ta) ta.value = (ta.value ? ta.value + '\n\n' : '') + rawText.trim();
    if (progress) progress.textContent = 'Text extracted and appended below';
    showToast('OCR text imported');
    stopFactsCamera();
  } catch (e) {
    if (progress) progress.textContent = 'OCR error: ' + (e instanceof Error ? e.message : String(e));
    wipeCanvas(c, c.getContext('2d'));
  } finally {
    if (btn) { btn.textContent = origLabel; btn.disabled = !factsCamStream; }
  }
}

// discardReviewCard — clears the review card without saving, does NOT re-open camera.
export async function discardReviewCard(): Promise<void> {
  const ok = await confirmApp('Clear Review Card', 'Discard this juror\'s scanned data without saving?', 'Discard', 'btn-danger');
  if (!ok) { showToast('Discard canceled'); return; }
  clearPending();
  const card = byId('ocr-review-card');
  if (card) card.style.display = 'none';
  showToast('Review card cleared — no data saved');
}

// ocrToPanel(j) — called after OCR confirmation; saves the juror and advances the
// counter. Builds a full Juror and hands it to the panel via loadJurorArray.
function ocrToPanel(j: Partial<Juror>): void {
  if (!j) { showToast('No OCR data'); return; }
  const juror: Juror = {
    id: 0, // assigned by loadJurorArray
    panelId: j.panelId || ('J-' + String(scanJurorCounter).padStart(2, '0')),
    seat: 0, // assigned by loadJurorArray
    gender: j.gender || '', age: j.age || '', edu: j.edu || '',
    empStatus: j.empStatus || '', bizType: j.bizType || '', occ: j.occ || '',
    hhSize: j.hhSize || '', parent: j.parent || '',
    spouseAge: j.spouseAge, spouseEdu: j.spouseEdu,
    jury: j.jury || '', le: j.le || 'None', lawEmp: j.lawEmp || 'None',
    victim: j.victim || '', accused: j.accused || '',
    fair: j.fair || '', caseKnow: j.caseKnow || '', opinion: j.opinion || '',
    notes: '', status: 'none', tags: [],
    ocrHash: j.ocrHash, // carry the cache key so per-juror delete can purge it
    questionnaire: j.questionnaire, // full extracted field map (nothing dropped)
    capturedAt: new Date().toISOString() // date/time this data was uploaded
  };
  loadJurorArray([juror]); // pushes + persists (store.save)
  updatePanelBadge();
  // Run scoring analysis on the newly added juror
  const state = store.getState();
  const ctx = panelContext();
  const savedJuror = state.jurors[state.jurors.length - 1];
  const sc = scoreJuror(savedJuror, ctx);
  const sl = getScoreLabel(sc, state.panelLens);
  const flags = getFlags(savedJuror, ctx);
  const flagSummary = flags.length ? flags.map(function (f) { return f.label; }).join(', ') : 'No flags';
  const side = state.panelLens === 'defense' ? 'DEF' : 'PRO';
  showToast('J-' + String(scanJurorCounter).padStart(2, '0') + ' • Score ' + sl.label + ' (' + side + ') • ' + flagSummary);
  // Refresh panel so scores are immediately visible
  renderPanel();
  scanJurorCounter++;
  // Auto-advance — update title and button label, re-enable button
  const nextLabel = 'Juror ' + scanJurorCounter;
  const title = byId('scan-juror-title');
  if (title) title.textContent = nextLabel;
  const btnLabel = byId('btn-cam-label');
  if (btnLabel) btnLabel.textContent = nextLabel;
  const progress = byId('ocr-progress');
  if (progress) progress.textContent = 'Tap to photograph the questionnaire';
  const btn = byId<HTMLButtonElement>('btn-cam-toggle');
  if (btn) btn.disabled = false;
  const inp = byId<HTMLInputElement>('cam-file-input');
  if (inp) inp.value = '';
}

// scanNext — kept for any legacy calls; ocrToPanel now auto-advances.
export function scanNext(): void {
  const nextLabel = 'Juror ' + scanJurorCounter;
  const title = byId('scan-juror-title');
  if (title) title.textContent = nextLabel;
  const btnLabel = byId('btn-cam-label');
  if (btnLabel) btnLabel.textContent = nextLabel;
  const progress = byId('ocr-progress');
  if (progress) progress.textContent = 'Tap to photograph the questionnaire';
  const inp = byId<HTMLInputElement>('cam-file-input');
  if (inp) inp.value = '';
}

export function scanDone(): void {
  showToast(store.getState().jurors.length + ' jurors in panel');
  scanJurorCounter = 1;
  // Free the handwriting model (weights + WASM heap) now the intake session is
  // over. Fire-and-forget; no-op if the template pipeline was never loaded.
  void import('@/ocr/form-parser')
    .then((m) => m.terminateHandwriting())
    .catch(() => { /* pipeline never loaded — nothing to free */ });
  // Reset the juror button label for next session
  const btn = byId('btn-take-photo');
  if (btn) { const lbl = btn.querySelector('span'); if (lbl) lbl.textContent = 'Juror 1'; }
  showPage('panel');
}
